import React from 'react'
import PropTypes from 'prop-types'
import {
  HeaderNavigation as BelleHeaderNavigation,
  Header,
  Dropdown,
  NavigationToggleButton,
  HeaderLogo,
  BaseComponent
} from 'belle-js'
import { NavLink } from 'redux-first-router-link'
import styles from './HeaderNavigation.module.scss'
import SidebarNavigation from '../SidebarNavigation/SidebarNavigation'
import classnames from 'classnames'
import logout, { samlIdpInitiatedUrl } from 'utils/Client/authentication.js'
import { trackAnalyticsEvent } from 'analytics'
import PhoneNumber from '../PhoneNumber/PhoneNumber'

const HeaderAccountSection = (props) => {
  const {
    name,
    children
  } = props

  return (
    <li className={styles.accountName}>
      <Dropdown
        label={name}
        toggleIcon="arrow"
        dropdownClass={styles.dropdown}
        testId="accountId">
        <ul className={styles.accountDropDown}>
          {React.Children.map(children, (child, index) =>
            <li key={index}>{child}</li>)}
        </ul>
      </Dropdown>
    </li>
  )
}

class HeaderNavigation extends BaseComponent {

  constructor (props) {
    super(props)

    this.bind('toggleMobileNav', 'handleToggleMobileNav')
  }

  toggleMobileNav () {
    const {
      clientNavOpen,
      openNav,
      closeNav
    } = this.props

    if (clientNavOpen) {
      closeNav()
    } else {
      openNav()
    }
  }

  handleToggleMobileNav () {
    this.toggleMobileNav()
  }

  handleLogoutOnClick (e) {
    logout('marketing')
  }

  getPortfolioLink () {
    const { user } = this.props
    if (user.blackDiamondSsoEnabled) {
      return (
        <a target="_blank" rel="noopener noreferrer" href={samlIdpInitiatedUrl}>Portfolio</a>
      )
    } else {
      return (
        <NavLink to="/portfolio" onClick={this.handleToggleMobileNav}>Portfolio</NavLink>
      )
    }
  }

  buildNavigationLinks () {
    const overview  = {
      name: 'overview',
      component : (
        <li key={1} data-test-id="overviewHeaderID">
          <NavLink onClick={this.handleToggleMobileNav} to="/dashboard">Overview</NavLink>
        </li>
      )
    }

    const portfolio  = {
      name: 'portfolio',
      component: (
        <li key={2} data-test-id="portfolioHeaderID">
          {this.getPortfolioLink()}
        </li>
      )
    }

    const documents  = {
      name: 'documents',
      component: (
        <li key={3} data-test-id="documentsHeaderID">
          <NavLink
            to="/documents/my-uploads"
            isActive={(match, location) => location.pathname.includes('/documents/')}
            onClick={() => {
              this.handleToggleMobileNav()
              trackAnalyticsEvent('Documents Clicked')
            }}>
            Documents
          </NavLink>
        </li>
      )
    }

    return [overview, portfolio, documents]
  }

  getNavigationLinks () {
    const { navigationLinks } = this.props

    return this.buildNavigationLinks()
      .filter(link => navigationLinks.map(showLink => showLink).includes(link.name))
      .map(link => link.component)
  }

  render () {
    const { teamMembers, user, isMobile, location, isLoading } = this.props

    const navigationData = {
      firstName: '',
      contactInfo: {
        firstName: '',
        mobilePhone: '',
        email: '',
      }
    }

    if (user && teamMembers.length > 0) {
      const contactInfo = teamMembers.find(
        member => member.id === user.financialAdvisorId
      )

      navigationData.firstName = user.firstName

      if (contactInfo) {
        navigationData.contactInfo = { ...contactInfo }
      }
    }

    const pathNamePages = {
      '/dashboard': 'Overview',
      '/portfolio': 'Portfolio',
      '/documents/tax-forms': 'Documents',
      '/documents/proposals': 'Documents',
      '/documents/other': 'Documents',
      '/documents/statements': 'Documents',
      '/documents/my-uploads': 'Documents',
      '/settings/login': 'Profile Settings',
      '/settings/personal': 'Profile Settings'
    }

    const currentTab = () => {
      const { pathname } = location

      return pathNamePages[pathname] || 'Overview'
    }

    const mobileNavigation = (
      <React.Fragment>
        <NavigationToggleButton onClick={this.handleToggleMobileNav} />
        <BelleHeaderNavigation>
          {currentTab()}
        </BelleHeaderNavigation>
      </React.Fragment>
    )

    const desktopNavigation = (
      <React.Fragment>
        <div className={styles.logo}>
          <HeaderLogo size={30} />
        </div>
        <BelleHeaderNavigation>
          {this.getNavigationLinks()}
          <HeaderAccountSection name={navigationData.firstName}>
            <div className={styles.divider} />
            <span
              data-test-id="contactDropdownId"
              className={classnames(styles.title, styles.section, styles.sectionWithChildren)}>
              {`Contact ${navigationData.contactInfo.firstName}`}
              <ul className={styles.link}>
                <li>
                  { navigationData.contactInfo.mobilePhone &&
                  <span data-test-id="telephoneDropdownId">
                    <PhoneNumber
                      number={navigationData.contactInfo.mobilePhone}
                      showIcon={false}
                      showLink={isMobile} />
                  </span>
                  }
                </li>
                <li>
                  {navigationData.contactInfo.email &&
                  <span data-test-id="emailDropdownId" className={classnames(styles.uppercased, styles.email)}>
                    <a
                      href={`mailto:${navigationData.contactInfo.email}`} >
                      {navigationData.contactInfo.email}
                    </a>
                  </span>
                  }
                </li>
              </ul>
            </span>
            <div className={styles.divider} />
            <div
              className={classnames(styles.dropdownMainSection, styles.section, styles.link)}
              data-test-id="profileSettingsDropdownId">
              <NavLink
                to="/settings/login"
                onClick={() => trackAnalyticsEvent('Profile Settings Clicked')}
                isActive={(match, location) => location.pathname.includes('/settings/')}>
                  Settings
              </NavLink>
            </div>
            <div className={styles.divider} />
            <div
              className={classnames(styles.dropdownMainSection, styles.section, styles.link)}
              data-test-id="logOutDropdownId"
              onClick={() => {
                this.handleLogoutOnClick()
                trackAnalyticsEvent('Logout Clicked')
              }}
              style={{ cursor: 'pointer' }}>
              Log Out
            </div>
          </HeaderAccountSection>
        </BelleHeaderNavigation>
      </React.Fragment>
    )

    return (
      <React.Fragment>
        {!isLoading && <SidebarNavigation
          isMobile={isMobile}
          isNavOpen={this.props.clientNavOpen}
          navigationLinks={this.getNavigationLinks()}
          closeNav={this.toggleMobileNav}
          location={location}
          contact={navigationData.contactInfo.firstName}
          email={navigationData.contactInfo.email}
          phone={navigationData.contactInfo.mobilePhone} />}
        {!isLoading &&
        <div className={styles.HeaderNavigation}>
          <Header isFixed={true} testId="ClientAppHeader">
            { isMobile ? mobileNavigation : desktopNavigation }
          </Header>
        </div>}
      </React.Fragment>
    )
  }

}

HeaderNavigation.defaultProps = {
  isMobile: false,
  isLoading: false,
  openNav () {},
  siteNavOpen: false,
  user: {},
  teamMembers: [],
  navigationLinks: ['overview', 'portfolio', 'documents']
}

HeaderNavigation.propTypes = {
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  openNav: PropTypes.func,
  location: PropTypes.object.isRequired,
  siteNavOpen: PropTypes.bool,
  user: PropTypes.object,
  teamMembers: PropTypes.array,
  navigationLinks: PropTypes.array
}


HeaderAccountSection.defaultProps = {
  name: '',
  children: [],
}

HeaderAccountSection.propTypes = {
  children: PropTypes.node,
  name: PropTypes.string,
}


export default HeaderNavigation



// WEBPACK FOOTER //
// ./src/components/Client/HeaderNavigation/HeaderNavigation.jsx